import { can, KR_HOLIDAYS, movableReason, type CenterEvent, type TherapySession } from '@selfon/domain/browser';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { api, type StaffRow } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { ChevronIcon, principalOf } from '../../components/Layout';
import { Alert, errorText, useLoad } from '../../components/ui';
import { todayKst } from '../../format';
import {
  addDays, addMonths, applyFilters, dateOf, dayLabel, hhmm, hm, hueOf, layoutLanes, loadPref, minOf, monthOf, monthsIn, savePref,
  moveProblem, sameSpot, STATUS_KEY_LABEL, STATUS_KEYS, statusKey, statusText, visibleDays, WEEKDAY, weekday,
  type Filters, type MoveTo, type StatusKey, type View,
} from './cal';
import { MobileFilters } from '../../components/MobileNav';
import { SearchSelect } from '../../components/SearchSelect';
import { attachPanelResize } from './panelResize';
import { AllDayDialog, EventDetailDialog, eventKindLabel, MoveDialog, ResizeDialog, ScheduleDialog, SessionDialog, type ResizeRequest, type SchedulePrefill } from './dialogs';
import './calendar.css';

const DAY_START = 8 * 60;   // 08:00
const DAY_END = 22 * 60;    // 22:00
const BASE_PPM = 1;         // 기본 배율: 1시간 = 60px
const ZOOM = { min: 0.75, max: 4, step: 0.25, key: 'selfon.calendar.zoom' } as const;
const clampZoom = (z: number) => Math.min(ZOOM.max, Math.max(ZOOM.min, Math.round(z / ZOOM.step) * ZOOM.step));
const SNAP = 10;            // 끌어 옮길 때 10분 단위
const PREF_KEY = 'selfon.calendar.v1';

interface Prefs { view: View; showSunday: boolean; filters: Filters }
const DEFAULT_PREFS: Prefs = {
  view: 'week', showSunday: false,
  filters: { hiddenStaff: [], statuses: [...STATUS_KEYS], clientId: '', program: '', voucher: '' },
};

/** 월별로 회기·종일 일정을 불러와 모아 둔다(보기가 바뀌어도 이미 받은 달은 다시 받지 않음). */
function useMonthData(centerId: string, months: string[]) {
  const [cache, setCache] = useState<Record<string, { sessions: TherapySession[]; events: CenterEvent[] }>>({});
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const key = months.join(',');
  useEffect(() => {
    let alive = true;
    const missing = months.filter((m) => !cache[m]);
    if (!missing.length) return;
    Promise.all(missing.map(async (m) => {
      const [s, e] = await Promise.all([api.sessions(centerId, m), api.events(centerId, m)]);
      return [m, { sessions: s.sessions, events: e.events }] as const;
    })).then((rows) => { if (alive) setCache((c) => ({ ...c, ...Object.fromEntries(rows) })); }).catch((e) => alive && setError(errorText(e)));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centerId, key, tick]);
  const loaded = months.every((m) => cache[m]);
  const sessions = useMemo(() => months.flatMap((m) => cache[m]?.sessions ?? []), [cache, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const events = useMemo(() => months.flatMap((m) => cache[m]?.events ?? []), [cache, key]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = useCallback(() => { setCache({}); setError(null); setTick((t) => t + 1); }, []);
  return { sessions, events, loaded, error, reload };
}

const SIDE_FOLD_KEY = 'selfon.calendar.side.folded';

/** 일정 화면 왼쪽 패널 접기. 이 브라우저에만 기억한다. */
function useSideFold(): [boolean, () => void] {
  const [folded, setFolded] = useState(() => { try { return localStorage.getItem(SIDE_FOLD_KEY) === '1'; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(SIDE_FOLD_KEY, folded ? '1' : '0'); } catch { /* 저장 불가 환경 */ } }, [folded]);
  return [folded, useCallback(() => setFolded((f) => !f), [])];
}

export function CalendarPage() {
  const { me } = useAuth();
  const p = principalOf(me!);
  const centerId = me!.centerId!;
  const canWrite = can(p, 'session:write');
  const [prefs, setPrefs] = useState<Prefs>(() => loadPref(PREF_KEY, DEFAULT_PREFS));
  useEffect(() => savePref(PREF_KEY, prefs), [prefs]);
  const { view, showSunday, filters } = prefs;
  const [cursor, setCursor] = useState(todayKst());
  const [drawer, setDrawer] = useState(false);
  const [sideFolded, toggleSide] = useSideFold();
  const [done, setDone] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<SchedulePrefill | null>(null);
  const [openSession, setOpenSession] = useState<TherapySession | null>(null);
  const [allDayDate, setAllDayDate] = useState<string | null>(null);
  const [openEvent, setOpenEvent] = useState<CenterEvent | null>(null);
  const [moving, setMoving] = useState<{ session: TherapySession; to: MoveTo | null } | null>(null);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [resizeReq, setResizeReq] = useState<ResizeRequest | null>(null);
  // 시간표 세로 배율(1 = 1시간 60px). 이 브라우저에만 기억.
  const [zoom, setZoomRaw] = useState(() => { const z = Number(loadPref<{ z: number }>(ZOOM.key, { z: 1 }).z); return Number.isFinite(z) ? clampZoom(z) : 1; });
  const setZoom = useCallback((z: number) => setZoomRaw(clampZoom(z)), []);
  useEffect(() => savePref(ZOOM.key, { z: zoom }), [zoom]);

  const clientsQ = useLoad(() => api.clients(centerId), [centerId]);
  const staffQ = useLoad(() => api.staff(centerId), [centerId]);
  const staff = useMemo(() => (staffQ.data?.staff ?? []).filter((s) => s.active), [staffQ.data]);
  const clients = clientsQ.data?.clients ?? [];
  const clientName = useMemo(() => new Map(clients.map((c) => [c.id, c.displayName])), [clients]);
  const staffById = useMemo(() => new Map(staff.map((s, i) => [s.userId, { ...s, hue: hueOf(i) }])), [staff]);

  const days = visibleDays(view, cursor, showSunday);
  const months = monthsIn(view === 'month' ? days : [...days, cursor]);
  const data = useMonthData(centerId, months);
  const shown = useMemo(() => applyFilters(data.sessions, filters).filter((s) => days.includes(dateOf(s.startAt))), [data.sessions, filters, days.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const setFilters = (f: Partial<Filters>) => setPrefs((x) => ({ ...x, filters: { ...x.filters, ...f } }));
  const setView = (v: View) => setPrefs((x) => ({ ...x, view: v }));
  const today = todayKst();

  const move = (dir: -1 | 1) => setCursor((c) => (view === 'month' ? addMonths(c, dir) : addDays(c, view === 'staff' ? dir : 7 * dir)));
  const title = view === 'month' ? `${cursor.slice(0, 4)}년 ${Number(cursor.slice(5, 7))}월`
    : view === 'staff' ? `${cursor.slice(0, 4)}년 ${Number(cursor.slice(5, 7))}월 ${dayLabel(cursor)}`
      : `${days[0]!.slice(0, 4)}년 ${Number(days[0]!.slice(5, 7))}월 ${Number(days[0]!.slice(8))}일 – ${Number(days.at(-1)!.slice(5, 7))}월 ${Number(days.at(-1)!.slice(8))}일`;

  const afterChange = (msg: string) => { setDone(msg); setMoveError(null); data.reload(); };
  // 끌어 옮기기: 예정·결제 전 회기만, 치료사는 자기 회기만
  const canDrag = (s: TherapySession) => canWrite && !movableReason(s) && (me!.role !== 'THERAPIST' || s.therapistId === me!.userId);
  const checkMove = (s: TherapySession, to: MoveTo) => moveProblem(s, to, data.sessions, me!, new Date().toISOString());
  const onDrop = (s: TherapySession, to: MoveTo) => {
    if (sameSpot(s, to)) return;
    const problem = checkMove(s, to);
    if (problem) { setDone(null); setMoveError(`옮기지 못했습니다: ${problem}`); return; }
    setMoveError(null);
    setMoving({ session: s, to });
  };
  const workspace = useRef<HTMLDivElement>(null);
  useEffect(() => (workspace.current ? attachPanelResize(workspace.current) : undefined), []);
  // 넓은 화면에서는 일정 화면이 창 높이에 꼭 맞게(calendar.css): 화면 위쪽에서 작업 영역까지의 거리를 알려 준다
  useLayoutEffect(() => {
    const el = workspace.current;
    if (!el) return undefined;
    const measure = () => el.style.setProperty('--ws-top', `${Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY))}px`);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  const programs = useMemo(() => [...new Set(data.sessions.map((s) => s.program))].sort(), [data.sessions]);
  const vouchers = useMemo(() => [...new Set(data.sessions.map((s) => s.voucherProgram).filter(Boolean) as string[])].sort(), [data.sessions]);

  const sidebar = (
    <aside className={`cal-side${drawer ? ' open' : ''}`} aria-label="일정 필터">
      {canWrite && <button type="button" className="btn btn-primary btn-block" onClick={() => setPrefill({ date: view === 'month' ? today : cursor })}>＋ 일정 등록</button>}
      <Panel title="날짜 선택" onReset={() => setCursor(today)} resetLabel="오늘로">
        <MiniMonth cursor={cursor} today={today} onPick={(d) => { setCursor(d); setDrawer(false); }} busyDates={new Set(data.sessions.map((s) => dateOf(s.startAt)))} />
      </Panel>
      <Panel title="선생님" onReset={() => setFilters({ hiddenStaff: filters.hiddenStaff.length ? [] : staff.map((s) => s.userId) })} resetLabel={filters.hiddenStaff.length ? '모두 보기' : '모두 끄기'} resetText>
        <ul className="staff-list">
          {staff.map((s, i) => {
            const on = !filters.hiddenStaff.includes(s.userId);
            return (
              <li key={s.userId}>
                <label className="staff-check" style={{ '--h': hueOf(i) } as CSSProperties} title={`${s.name} · ${s.title}`}>
                  <input type="checkbox" checked={on} onChange={() => setFilters({ hiddenStaff: on ? [...filters.hiddenStaff, s.userId] : filters.hiddenStaff.filter((x) => x !== s.userId) })} />
                  <span className="swatch" aria-hidden="true" />
                  <span>{s.name} <span className="muted small">/ {s.title}</span></span>
                </label>
              </li>
            );
          })}
        </ul>
      </Panel>
      <Panel title="검색 조건" onReset={() => setFilters({ clientId: '', program: '', voucher: '' })} resetLabel="검색 조건 초기화">
        <SearchSelect label="이용자" value={filters.clientId} onChange={(v) => setFilters({ clientId: v })}
          options={[{ value: '', label: '이용자 전체' }, ...[...clients].sort((a, b) => a.displayName.localeCompare(b.displayName, 'ko')).map((c) => ({ value: c.id, label: c.displayName, detail: `${c.birthYear}년생` }))]} />
        <SearchSelect label="프로그램" value={filters.program} onChange={(v) => setFilters({ program: v })}
          options={[{ value: '', label: '프로그램 전체' }, ...programs.map((x) => ({ value: x, label: x }))]} />
        <SearchSelect label="바우처" value={filters.voucher} onChange={(v) => setFilters({ voucher: v })}
          options={[{ value: '', label: '바우처 전체' }, { value: '__none', label: '비바우처' }, ...vouchers.map((x) => ({ value: x, label: x }))]} />
      </Panel>
      <Panel title="표시할 상태" onReset={() => setFilters({ statuses: [...STATUS_KEYS] })} resetLabel="모두 표시">
        <div className="status-toggles">
          {STATUS_KEYS.map((k) => {
            const on = filters.statuses.includes(k);
            return <button key={k} type="button" aria-pressed={on} className={`toggle st-${k}${on ? ' on' : ''}`}
              onClick={() => setFilters({ statuses: on ? filters.statuses.filter((x) => x !== k) : [...filters.statuses, k] })}>{STATUS_KEY_LABEL[k]}</button>;
          })}
        </div>
        <label className="inline-check"><input type="checkbox" checked={showSunday} onChange={(e) => setPrefs((x) => ({ ...x, showSunday: e.target.checked }))} /> 일요일 표시</label>
      </Panel>
    </aside>
  );

  return (
    <div className={`cal schedule-workspace${sideFolded ? ' side-folded' : ''}`} ref={workspace}>
      <MobileFilters label="날짜·선생님·수업 필터">{sidebar}</MobileFilters>
      {drawer && <button type="button" className="cal-scrim" aria-label="필터 닫기" onClick={() => setDrawer(false)} />}
      <section className="cal-main schedule-panel" aria-label="일정 달력">
        <div className="schedule-panel-heading">
          <span className="schedule-heading-start">
            {/* 넓은 화면에서 왼쪽 패널(날짜·선생님·검색 조건)을 접어 시간표를 넓게 본다 */}
            <button type="button" className="side-fold-btn" onClick={toggleSide} aria-expanded={!sideFolded} aria-label={sideFolded ? '왼쪽 패널 펼치기' : '왼쪽 패널 접기'}
              title={sideFolded ? '날짜·선생님·검색 조건 펼치기' : '날짜·선생님·검색 조건 접기'}><ChevronIcon dir={sideFolded ? 'right' : 'left'} size={14} /></button>
            <span>시간표</span>
            {sideFolded && canWrite && <button type="button" className="btn btn-primary btn-sm side-fold-add" onClick={() => setPrefill({ date: view === 'month' ? today : cursor })}>＋ 일정 등록</button>}
          </span>
          <button type="button" className="link" onClick={data.reload} aria-label="새로고침" title="새로고침">⟳</button>
        </div>
        <div className="schedule-panel-content">
        <div className="cal-toolbar">
          <CalendarMobileBar title={title} date={cursor} view={view} onView={setView} onDate={setCursor} onToday={() => setCursor(today)} onMove={move}
            canAdd={canWrite} ready={data.loaded} onAdd={() => setPrefill({ date: view === 'month' ? today : cursor, startTime: '09:00' })} />
          <div className="btn-row">
            <button type="button" className="btn btn-ghost cal-filter-btn" onClick={() => setDrawer(true)}>필터</button>
            <div className="seg" role="group" aria-label="이동">
              <button type="button" className="btn" onClick={() => move(-1)} aria-label="이전">‹</button>
              <button type="button" className="btn" onClick={() => setCursor(today)}>오늘</button>
              <button type="button" className="btn" onClick={() => move(1)} aria-label="다음">›</button>
            </div>
            <h1 className="cal-title" aria-live="polite">{title}</h1>
          </div>
          <div className="btn-row">
            <div className="seg" role="group" aria-label="보기">
              {([['staff', '선생님별'], ['week', '주'], ['month', '월'], ['list', '목록']] as const).map(([v, label]) => (
                <button key={v} type="button" className={`btn${view === v ? ' active' : ''}`} aria-pressed={view === v} onClick={() => setView(v)}>{label}</button>
              ))}
            </div>
          </div>
        </div>
        {done && <Alert kind="ok">{done}</Alert>}
        {moveError && <Alert>{moveError}</Alert>}
        {(data.error || clientsQ.error || staffQ.error) && <Alert>{data.error ?? clientsQ.error ?? staffQ.error}</Alert>}
        <div className="cal-legend small muted">{shown.length}건</div>

        {view === 'staff' && data.events.some((e) => e.date === cursor && !e.staffId) && (
          // 선생님별 보기: 센터 전체 일정은 칸마다 반복하지 않고 위에 한 번만
          <div className="cal-banner">
            {data.events.filter((e) => e.date === cursor && !e.staffId).map((e) => (
              <button key={e.id} type="button" className={`allday allday-${e.kind}`} onClick={() => setOpenEvent(e)}>{eventKindLabel(e.kind)} · {e.title}</button>
            ))}
          </div>
        )}
        {(view === 'week' || view === 'staff') && (
          <div className="tg-zoom" role="group" aria-label="시간표 높이">
            <label htmlFor="tg-zoom-range">시간 간격</label>
            <input id="tg-zoom-range" type="range" min={ZOOM.min} max={ZOOM.max} step={ZOOM.step} value={zoom}
              aria-valuetext={`${Math.round(60 * zoom * BASE_PPM)}픽셀 / 시간`} onChange={(e) => setZoom(Number(e.target.value))} />
            <span className="tg-zoom-val" aria-hidden="true">{zoom.toFixed(2).replace(/0$/, '')}배</span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setZoom(1)} disabled={zoom === 1}>기본 크기</button>
            <span className="muted small tg-zoom-hint">08:00–22:00 · Ctrl + 휠로 높이 조절</span>
          </div>
        )}
        {(view === 'week' || view === 'staff') && (
          <TimeGrid zoom={zoom} onZoom={setZoom}
            columns={view === 'week'
              ? days.map((d) => ({ key: d, date: d, label: dayLabel(d), holiday: KR_HOLIDAYS[d], today: d === today, sessions: shown.filter((s) => dateOf(s.startAt) === d), events: data.events.filter((e) => e.date === d) }))
              : staff.filter((s) => !filters.hiddenStaff.includes(s.userId)).map((s) => ({ key: s.userId, date: cursor, label: s.name, sub: s.title, today: false, therapistId: s.userId, holiday: undefined,
                sessions: shown.filter((x) => x.therapistId === s.userId), events: data.events.filter((e) => e.date === cursor && e.staffId === s.userId) }))}
            nowDate={today} staffById={staffById} clientName={clientName} canWrite={canWrite} loaded={data.loaded}
            onSlot={(date, min, therapistId) => setPrefill({ date, startTime: hhmm(min), therapistId })}
            onOpen={setOpenSession} onAllDay={(d) => canWrite && setAllDayDate(d)} onOpenEvent={setOpenEvent}
            canDrag={canDrag} checkMove={checkMove} onDrop={onDrop} onResize={(s, start, end) => { setMoveError(null); setResizeReq({ session: s, start, end }); }} />
        )}
        {view === 'month' && (
          <MonthGrid days={days} cursor={cursor} today={today} sessions={shown} events={data.events} staffById={staffById} clientName={clientName}
            onDay={(d) => { setCursor(d); setView('staff'); }} onOpen={setOpenSession} onOpenEvent={setOpenEvent}
            canDrag={canDrag} checkMove={checkMove} onDrop={onDrop} />
        )}
        {view === 'list' && <AgendaList days={days} sessions={shown} events={data.events} staffById={staffById} clientName={clientName} onOpen={setOpenSession} />}
        </div>
      </section>

      <ScheduleDialog open={Boolean(prefill)} prefill={prefill ?? {}} clients={clients} staff={staffQ.data?.staff ?? []} onClose={() => setPrefill(null)}
        onCreated={(list) => { const f = list[0]!; afterChange(`${clientName.get(f.clientId) ?? '이용자'} 일정 ${list.length}건을 등록했습니다 (${dayLabel(dateOf(f.startAt))} ${hm(f.startAt)}${list.length > 1 ? '부터 매주' : ''}).`); setCursor(dateOf(f.startAt)); }} />
      <SessionDialog session={openSession} clientName={openSession ? clientName.get(openSession.clientId) ?? '이용자' : ''} staff={openSession ? staffById.get(openSession.therapistId) : undefined}
        canWrite={canWrite} canMove={openSession ? canDrag(openSession) : false} onClose={() => setOpenSession(null)} onChanged={afterChange}
        onMove={(s) => { setOpenSession(null); setMoving({ session: s, to: null }); }}
        onResize={(s) => { setOpenSession(null); setResizeReq({ session: s, start: hm(s.startAt), end: hm(s.endAt) }); }} />
      <ResizeDialog req={resizeReq} all={data.sessions} clientName={resizeReq ? clientName.get(resizeReq.session.clientId) ?? '이용자' : ''}
        onClose={() => setResizeReq(null)} onSaved={afterChange} />
      <MoveDialog session={moving?.session ?? null} initial={moving?.to ?? null} all={data.sessions} events={data.events} staff={staffQ.data?.staff ?? []}
        clientName={moving ? clientName.get(moving.session.clientId) ?? '이용자' : ''} onClose={() => setMoving(null)}
        onMoved={(msg, s) => { afterChange(msg); if (view !== 'staff' || dateOf(s.startAt) !== cursor) setCursor(dateOf(s.startAt)); }} />
      <AllDayDialog date={allDayDate} staff={staffQ.data?.staff ?? []} onClose={() => setAllDayDate(null)} onSaved={afterChange} />
      <EventDetailDialog event={openEvent} staffName={openEvent?.staffId ? staffById.get(openEvent.staffId)?.name : undefined}
        canCancel={canWrite && (me!.role !== 'THERAPIST' || openEvent?.staffId === me!.userId)} onClose={() => setOpenEvent(null)} onCancelled={afterChange} />
    </div>
  );
}

type StaffInfo = StaffRow & { hue: number };

function EventBlock({ s, staff, client, style, compact, draggable, dragging, resizing, timeText, onOpen, onPointerDown, onResizeStart, onResizeKey }: {
  s: TherapySession; staff?: StaffInfo; client: string; style?: CSSProperties; compact?: boolean; draggable?: boolean; dragging?: boolean; resizing?: boolean; timeText?: string;
  onOpen: () => void; onPointerDown?: (e: ReactPointerEvent<HTMLButtonElement>) => void;
  onResizeStart?: (edge: 'start' | 'end', e: ReactPointerEvent<HTMLButtonElement>) => void; onResizeKey?: (edge: 'start' | 'end', dir: -1 | 1) => void;
}) {
  const key = statusKey(s);
  const label = `${hm(s.startAt)}–${hm(s.endAt)} ${client} · ${s.program}${s.voucherProgram ? ` · ${s.voucherProgram}` : ''} · ${staff?.name ?? ''} · ${statusText(s)}${s.movedFrom ? ' · 옮긴 일정' : ''}`;
  const handle = (edge: 'start' | 'end') => draggable && onResizeStart && (
    <button type="button" className={`ev-resize-handle ev-resize-${edge}`} aria-label={`${client} 수업 ${edge === 'start' ? '시작' : '종료'} 시간 조절`}
      title={`${edge === 'start' ? '위' : '아래'} 경계를 끌어 시간 조절 · 방향키로 10분 조절`}
      onPointerDown={(e) => onResizeStart(edge, e)} onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); onResizeKey?.(edge, e.key === 'ArrowUp' ? -1 : 1); } }}><span /></button>
  );
  return (
    <div className={`ev ev-${key}${compact ? ' ev-compact' : ''}${draggable ? ' ev-drag' : ''}${dragging ? ' ev-dragging' : ''}${resizing ? ' ev-resizing' : ''}`}
      style={{ '--h': staff?.hue ?? 220, ...style } as CSSProperties}>
      {handle('start')}
      <button type="button" className="ev-open" onClick={(e) => { e.stopPropagation(); onOpen(); }} title={label} aria-label={label}
        onPointerDown={draggable ? onPointerDown : undefined} onContextMenu={draggable ? (e) => e.preventDefault() : undefined}>
        {/* 두 줄: 1줄 이름 + 오른쪽 시간, 2줄 상태·프로그램(넘치면 잘림) */}
        <span className="ev-l1">
          <span className="ev-name">{client}</span>
          <span className="ev-time">{timeText ?? hm(s.startAt)}{s.movedFrom && <span className="ev-moved" aria-hidden="true"> ⇄</span>}</span>
        </span>
        <span className="ev-sub">{key !== 'SCHEDULED' && <span className="ev-st">{statusText(s)} · </span>}{compact ? '' : `${s.program}${s.voucherProgram ? ' · 바우처' : ''}`}</span>
      </button>
      {handle('end')}
    </div>
  );
}

interface Column { key: string; date: string; label: string; sub?: string; holiday?: string; today: boolean; therapistId?: string; sessions: TherapySession[]; events: CenterEvent[] }

/** 시간표(주·선생님별). 1분 = 1px, 겹치는 일정은 나란히. 오늘 칸 강조와 현재 시각선. */
interface Drag { s: TherapySession; grabMin: number; x0: number; y0: number; armed: boolean; active: boolean; col: number; min: number }

function TimeGrid({ zoom, onZoom, columns, nowDate, staffById, clientName, canWrite, loaded, onSlot, onOpen, onAllDay, onOpenEvent, canDrag, checkMove, onDrop, onResize }: {
  zoom: number; onZoom: (z: number) => void;
  columns: Column[]; nowDate: string; staffById: Map<string, StaffInfo>; clientName: Map<string, string>; canWrite: boolean; loaded: boolean;
  onSlot: (date: string, min: number, therapistId?: string) => void; onOpen: (s: TherapySession) => void; onAllDay: (date: string) => void; onOpenEvent: (e: CenterEvent) => void;
  canDrag: (s: TherapySession) => boolean; checkMove: (s: TherapySession, to: MoveTo) => string | null; onDrop: (s: TherapySession, to: MoveTo) => void;
  onResize: (s: TherapySession, start: string, end: string) => void;
}) {
  const PX_PER_MIN = BASE_PPM * zoom; // 이 시간표 안에서는 배율을 곱한 값
  const scroller = useRef<HTMLDivElement>(null);
  const colEls = useRef<Array<HTMLDivElement | null>>([]);
  const [drag, setDrag] = useState<Drag | null>(null);
  const dragRef = useRef<Drag | null>(null);
  const justDragged = useRef(false); // 놓은 직후 따라오는 click 이 상세·등록 창을 열지 않게
  const latest = useRef({ columns, onDrop });
  latest.current = { columns, onDrop };
  const targetOf = (d: Drag): MoveTo => {
    const c = latest.current.columns[d.col]!;
    return { date: c.date, startTime: hhmm(d.min), therapistId: c.therapistId ?? d.s.therapistId };
  };

  /**
   * 마우스는 누른 채 5px 움직이면, 터치는 0.35초 길게 누른 뒤부터 끌기 시작(그 전에 움직이면 평소처럼 스크롤).
   * 칸은 포인터 x 로, 시각은 y 로 정하고 10분 단위로 맞춘다. 위·아래 끝에 닿으면 자동 스크롤.
   */
  const startDrag = (s: TherapySession, colIndex: number, e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const touch = e.pointerType === 'touch';
    const d: Drag = { s, grabMin: Math.round((e.clientY - rect.top) / PX_PER_MIN), x0: e.clientX, y0: e.clientY, armed: !touch, active: false, col: colIndex, min: minOf(s.startAt) };
    dragRef.current = d;
    const timer = touch ? window.setTimeout(() => { if (dragRef.current === d) { d.armed = d.active = true; setDrag({ ...d }); navigator.vibrate?.(15); } }, 350) : 0;
    const dur = minOf(s.endAt) - minOf(s.startAt);
    const move = (ev: PointerEvent) => {
      if (dragRef.current !== d) return;
      const dist = Math.hypot(ev.clientX - d.x0, ev.clientY - d.y0);
      if (!d.armed) { if (dist > 8) end(); return; }
      if (!d.active) { if (dist < 5) return; d.active = true; }
      const rects = colEls.current.slice(0, latest.current.columns.length).map((el) => el!.getBoundingClientRect());
      let col = rects.findIndex((r) => ev.clientX >= r.left && ev.clientX < r.right);
      if (col < 0) col = ev.clientX < rects[0]!.left ? 0 : rects.length - 1;
      const raw = DAY_START + Math.round(((ev.clientY - rects[col]!.top) / PX_PER_MIN - d.grabMin) / SNAP) * SNAP;
      d.col = col;
      d.min = Math.max(DAY_START, Math.min(DAY_END - dur, raw));
      const sc = scroller.current;
      if (sc) { const r = sc.getBoundingClientRect(); if (ev.clientY < r.top + 32) sc.scrollTop -= 14; else if (ev.clientY > r.bottom - 32) sc.scrollTop += 14; }
      setDrag({ ...d });
    };
    const blockScroll = (ev: TouchEvent) => { if (dragRef.current === d && d.armed) ev.preventDefault(); };
    const up = () => {
      const was = dragRef.current === d && d.active;
      end();
      if (!was) return;
      justDragged.current = true;
      window.setTimeout(() => { justDragged.current = false; }, 0);
      latest.current.onDrop(s, targetOf(d));
    };
    const end = () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', end);
      window.removeEventListener('touchmove', blockScroll);
      if (dragRef.current === d) { dragRef.current = null; setDrag(null); }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', end);
    window.addEventListener('touchmove', blockScroll, { passive: false });
  };
  // Esc 로 끌기 취소
  useEffect(() => {
    if (!drag) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { dragRef.current = null; setDrag(null); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drag]);
  /** 수업 시간 조절: 위 끝은 시작, 아래 끝은 종료를 10분 단위로(10~240분, 화면에 보이는 08:00~22:00 안). 놓으면 확인 창. */
  const [resizing, setResizing] = useState<{ id: string; start: number; end: number } | null>(null);
  const resizeRange = (s: TherapySession, edge: 'start' | 'end', shift: number) => {
    const st0 = minOf(s.startAt), en0 = minOf(s.endAt);
    return edge === 'start'
      ? { start: Math.max(DAY_START, en0 - 240, Math.min(en0 - 10, st0 + shift)), end: en0 }
      : { start: st0, end: Math.min(DAY_END, st0 + 240, Math.max(st0 + 10, en0 + shift)) };
  };
  const startResize = (s: TherapySession, edge: 'start' | 'end', e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault(); e.stopPropagation();
    const y0 = e.clientY;
    let cur = { id: s.id, start: minOf(s.startAt), end: minOf(s.endAt) };
    setResizing(cur);
    const move = (ev: PointerEvent) => { cur = { id: s.id, ...resizeRange(s, edge, Math.round((ev.clientY - y0) / PX_PER_MIN / SNAP) * SNAP) }; setResizing(cur); };
    const up = () => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up);
      setResizing(null);
      justDragged.current = true;
      window.setTimeout(() => { justDragged.current = false; }, 0);
      if (cur.start !== minOf(s.startAt) || cur.end !== minOf(s.endAt)) onResize(s, hhmm(cur.start), hhmm(cur.end));
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
  };
  const keyResize = (s: TherapySession, edge: 'start' | 'end', dir: -1 | 1) => {
    const r = resizeRange(s, edge, dir * SNAP);
    if (r.start !== minOf(s.startAt) || r.end !== minOf(s.endAt)) onResize(s, hhmm(r.start), hhmm(r.end));
  };
  const ghost = drag?.active ? (() => {
    const to = targetOf(drag);
    const dur = minOf(drag.s.endAt) - minOf(drag.s.startAt);
    return { col: drag.col, min: drag.min, to, dur, problem: sameSpot(drag.s, to) ? null : checkMove(drag.s, to), same: sameSpot(drag.s, to) };
  })() : null;
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = window.setInterval(() => setNow(new Date()), 60_000); return () => window.clearInterval(t); }, []);
  const nowMin = (() => { const k = new Date(now.getTime() + 9 * 3600_000); return k.getUTCHours() * 60 + k.getUTCMinutes(); })();
  /*
   * Ctrl + 휠: 포인터가 가리키는 시각이 그 자리에 머물도록 배율을 바꾼다. 끌기·시간 조절 중에는 무시.
   * 브라우저 확대(Ctrl + 휠)를 막아야 하므로 passive:false 로 직접 붙인다.
   */
  const anchor = useRef<{ min: number; y: number } | null>(null);
  const zoomRef = useRef({ zoom, onZoom, busy: false });
  zoomRef.current = { zoom, onZoom, busy: Boolean(dragRef.current) || resizing !== null };
  useEffect(() => {
    const sc = scroller.current;
    if (!sc) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      const z = zoomRef.current;
      if (z.busy || !e.deltaY) return;
      const next = clampZoom(z.zoom + (e.deltaY < 0 ? ZOOM.step : -ZOOM.step));
      if (next === z.zoom) return;
      const grid = sc.firstElementChild!.getBoundingClientRect();
      anchor.current = { min: (e.clientY - grid.top) / (BASE_PPM * z.zoom), y: e.clientY };
      z.onZoom(next);
    };
    sc.addEventListener('wheel', onWheel, { passive: false });
    return () => sc.removeEventListener('wheel', onWheel);
  }, []);
  // 배율이 바뀌면: 휠이면 포인터 위치 기준, 슬라이더면 보이는 영역 가운데 기준으로 스크롤을 맞춘다
  const prevZoom = useRef(zoom);
  useLayoutEffect(() => {
    const sc = scroller.current;
    if (!sc || prevZoom.current === zoom) return;
    // 시간표 본문이 스스로 스크롤되면 본문을, 아니면(화면 전체가 길어지는 배치) 창을 움직인다
    const view = sc.getBoundingClientRect();
    const midY = Math.max(view.top, 0) + Math.min(view.height, window.innerHeight) / 2;
    const gridTop0 = sc.firstElementChild!.getBoundingClientRect().top;
    const a = anchor.current ?? { min: (midY - gridTop0) / (BASE_PPM * prevZoom.current), y: midY };
    const delta = gridTop0 + a.min * BASE_PPM * zoom - a.y;
    const before = sc.scrollTop;
    sc.scrollTop = Math.max(0, before + delta);
    const rest = delta - (sc.scrollTop - before);
    if (Math.abs(rest) >= 1) window.scrollBy(0, rest);
    anchor.current = null;
    prevZoom.current = zoom;
  }, [zoom]);
  // 처음 열 때 가장 이른 일정(없으면 13시) 근처로 스크롤
  const first = Math.min(...columns.flatMap((c) => c.sessions.map((s) => minOf(s.startAt))), 13 * 60);
  useEffect(() => { if (scroller.current) scroller.current.scrollTop = Math.max(0, (first - DAY_START - 30) * PX_PER_MIN); }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const hours = Array.from({ length: (DAY_END - DAY_START) / 60 }, (_, i) => DAY_START + i * 60);
  const tmpl = { '--cols': columns.length, '--zoom': zoom } as CSSProperties;

  return (
    <div className={`tg${ghost ? ' tg-dragging' : ''}`} style={tmpl}>
      <div className="tg-head" role="row">
        <div className="tg-gutter" />
        {columns.map((c) => (
          <div key={c.key} className={`tg-colhead${c.today ? ' today' : ''}${c.holiday || weekday(c.date) === 0 ? ' holiday' : ''}`} role="columnheader">
            <strong>{c.label}</strong>{c.sub && <span className="muted small"> {c.sub}</span>}{c.holiday && <span className="hol"> {c.holiday}</span>}
          </div>
        ))}
      </div>
      <div className="tg-allday" role="row">
        <div className="tg-gutter small muted">종일</div>
        {columns.map((c) => (
          <div key={c.key} className={`tg-allcell${c.today ? ' today' : ''}`} onClick={() => onAllDay(c.date)} role="gridcell"
            aria-label={canWrite ? `${dayLabel(c.date)} 종일 일정 추가` : undefined}>
            {c.events.map((e) => (
              <button key={e.id} type="button" className={`allday allday-${e.kind}`} style={{ '--h': e.staffId ? staffById.get(e.staffId)?.hue ?? 220 : 220 } as CSSProperties}
                onClick={(ev) => { ev.stopPropagation(); onOpenEvent(e); }} title={`${eventKindLabel(e.kind)} · ${e.title}`}>
                {e.staffId && <b>{staffById.get(e.staffId)?.name} </b>}{e.title}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div className="tg-body" ref={scroller} tabIndex={0} aria-label="시간표 (세로로 스크롤)">
        <div className="tg-grid" style={{ height: (DAY_END - DAY_START) * PX_PER_MIN }}>
          <div className="tg-gutter tg-hours">{hours.map((h) => <div key={h} className="tg-hour" style={{ top: (h - DAY_START) * PX_PER_MIN }}>{h < 12 * 60 ? `오전 ${h / 60}시` : `오후 ${h / 60 === 12 ? 12 : h / 60 - 12}시`}</div>)}</div>
          {columns.map((c, ci) => (
            <div key={c.key} ref={(el) => { colEls.current[ci] = el; }} className={`tg-col${c.today ? ' today' : ''}${c.holiday ? ' holiday' : ''}${ghost?.col === ci ? ' drop-target' : ''}`}
              onClick={(e) => {
                if (!canWrite || justDragged.current) return;
                const y = e.clientY - (e.currentTarget as HTMLElement).getBoundingClientRect().top;
                const min = Math.min(DAY_END - 30, DAY_START + Math.floor(y / PX_PER_MIN / 30) * 30);
                onSlot(c.date, min, c.therapistId);
              }}>
              {hours.map((h) => <div key={h} className="tg-line" style={{ top: (h - DAY_START) * PX_PER_MIN }} />)}
              {layoutLanes(c.sessions).map(({ item, lane, lanes }) => {
                const rz = resizing?.id === item.id ? resizing : null;
                const st = rz ? rz.start : minOf(item.startAt), en = rz ? rz.end : minOf(item.endAt);
                const top = (Math.max(DAY_START, st) - DAY_START) * PX_PER_MIN;
                const height = Math.max(22, (en - st) * PX_PER_MIN - 2);
                return <EventBlock key={item.id} s={item} staff={staffById.get(item.therapistId)} client={clientName.get(item.clientId) ?? '이용자'} compact={lanes > 2}
                  style={{ top, height, left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)` }}
                  draggable={canDrag(item)} dragging={ghost !== null && drag?.s.id === item.id} resizing={Boolean(rz)} timeText={rz ? `${hhmm(rz.start)}–${hhmm(rz.end)}` : undefined}
                  onOpen={() => { if (!justDragged.current) onOpen(item); }} onPointerDown={(e) => startDrag(item, ci, e)}
                  onResizeStart={(edge, e) => startResize(item, edge, e)} onResizeKey={(edge, dir) => keyResize(item, edge, dir)} />;
              })}
              {ghost?.col === ci && (
                <div className={`ev-ghost${ghost.problem ? ' bad' : ''}`} aria-hidden="true"
                  style={{ top: (ghost.min - DAY_START) * PX_PER_MIN, height: Math.max(22, ghost.dur * PX_PER_MIN - 2), '--h': staffById.get(ghost.to.therapistId)?.hue ?? 220 } as CSSProperties}>
                  <b>{ghost.to.startTime}–{hhmm(ghost.min + ghost.dur)}</b>
                  <span>{ghost.same ? '원래 자리' : ghost.problem ?? '여기에 놓기'}</span>
                </div>
              )}
              {c.date === nowDate && nowMin >= DAY_START && nowMin <= DAY_END && <div className="tg-now" style={{ top: (nowMin - DAY_START) * PX_PER_MIN }} aria-hidden="true" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** 월간 달력: 날짜마다 최대 4건 + "+N개" (누르면 그날 선생님별 보기). 일정을 다른 날짜로 끌면 같은 시각·선생님으로 옮긴다(마우스). */
function MonthGrid({ days, cursor, today, sessions, events, staffById, clientName, onDay, onOpen, onOpenEvent, canDrag, checkMove, onDrop }: {
  days: string[]; cursor: string; today: string; sessions: TherapySession[]; events: CenterEvent[]; staffById: Map<string, StaffInfo>; clientName: Map<string, string>;
  onDay: (d: string) => void; onOpen: (s: TherapySession) => void; onOpenEvent: (e: CenterEvent) => void;
  canDrag: (s: TherapySession) => boolean; checkMove: (s: TherapySession, to: MoveTo) => string | null; onDrop: (s: TherapySession, to: MoveTo) => void;
}) {
  const [dragged, setDragged] = useState<TherapySession | null>(null);
  const [over, setOver] = useState<{ date: string; problem: string | null } | null>(null);
  const toOn = (s: TherapySession, date: string): MoveTo => ({ date, startTime: hm(s.startAt), therapistId: s.therapistId });
  const cols = days.some((d) => weekday(d) === 0) ? 7 : 6; // 일요일 표시 여부
  const byDate = useMemo(() => {
    const m = new Map<string, TherapySession[]>();
    for (const s of sessions) { const d = dateOf(s.startAt); m.set(d, [...(m.get(d) ?? []), s]); }
    for (const l of m.values()) l.sort((a, b) => a.startAt.localeCompare(b.startAt));
    return m;
  }, [sessions]);
  const heads = days.slice(0, cols).map((d) => WEEKDAY[weekday(d)]);
  return (
    <div className="mg" style={{ '--cols': cols } as CSSProperties} role="grid" aria-label="월간 달력">
      {heads.map((h) => <div key={h} className={`mg-head${h === '일' ? ' sun' : h === '토' ? ' sat' : ''}`} role="columnheader">{h}</div>)}
      {days.map((d) => {
        const list = byDate.get(d) ?? [];
        const evs = events.filter((e) => e.date === d);
        const out = monthOf(d) !== monthOf(cursor);
        const hol = KR_HOLIDAYS[d];
        return (
          <div key={d} className={`mg-cell${out ? ' out' : ''}${d === today ? ' today' : ''}${over?.date === d ? (over.problem ? ' drop-bad' : ' drop-ok') : ''}`} role="gridcell"
            title={over?.date === d && over.problem ? over.problem : undefined}
            onDragOver={dragged ? (e) => {
              e.preventDefault();
              if (over?.date !== d) setOver({ date: d, problem: dateOf(dragged.startAt) === d ? null : checkMove(dragged, toOn(dragged, d)) });
            } : undefined}
            onDrop={dragged ? (e) => { e.preventDefault(); const s = dragged; setDragged(null); setOver(null); onDrop(s, toOn(s, d)); } : undefined}>
            <button type="button" className={`mg-date${hol || weekday(d) === 0 ? ' red' : weekday(d) === 6 ? ' blue' : ''}`} onClick={() => onDay(d)} aria-label={`${dayLabel(d)} 선생님별 보기`}>
              {Number(d.slice(8))}{hol && <span className="hol"> {hol}</span>}
            </button>
            {evs.map((e) => <button key={e.id} type="button" className={`allday allday-${e.kind}`} style={{ '--h': e.staffId ? staffById.get(e.staffId)?.hue ?? 220 : 220 } as CSSProperties} onClick={() => onOpenEvent(e)}>
              {e.staffId && <b>{staffById.get(e.staffId)?.name} </b>}{e.title}</button>)}
            {list.slice(0, 4).map((s) => {
              const st = staffById.get(s.therapistId);
              return (
                <button key={s.id} type="button" className={`mg-ev mg-${statusKey(s)}${dragged?.id === s.id ? ' ev-dragging' : ''}`} style={{ '--h': st?.hue ?? 220 } as CSSProperties} onClick={() => onOpen(s)}
                  draggable={canDrag(s)} onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', s.id); setDragged(s); }}
                  onDragEnd={() => { setDragged(null); setOver(null); }}
                  title={`${hm(s.startAt)} ${clientName.get(s.clientId) ?? ''} · ${s.program} · ${st?.name ?? ''} · ${statusText(s)}`}>
                  <span className="dot" aria-hidden="true" />{hm(s.startAt)}{s.movedFrom && <span className="ev-moved" aria-hidden="true">⇄</span>} {s.status === 'CANCELLED' && <em>{statusText(s)} </em>}[{clientName.get(s.clientId) ?? '이용자'}] {s.program}
                </button>
              );
            })}
            {list.length > 4 && <button type="button" className="mg-more" onClick={() => onDay(d)}>+{list.length - 4}개</button>}
          </div>
        );
      })}
    </div>
  );
}

/** 목록: 날짜별로 묶은 일정(주 단위) */
function AgendaList({ days, sessions, events, staffById, clientName, onOpen }: {
  days: string[]; sessions: TherapySession[]; events: CenterEvent[]; staffById: Map<string, StaffInfo>; clientName: Map<string, string>; onOpen: (s: TherapySession) => void;
}) {
  return (
    <div className="agenda">
      {days.map((d) => {
        const list = sessions.filter((s) => dateOf(s.startAt) === d).sort((a, b) => a.startAt.localeCompare(b.startAt));
        const evs = events.filter((e) => e.date === d);
        return (
          <section key={d} className="agenda-day" data-day={Number(d.slice(8))} data-today={d === todayKst()} aria-label={dayLabel(d)}>
            <h2 className={`agenda-date${KR_HOLIDAYS[d] ? ' red' : ''}`}>{dayLabel(d)}{KR_HOLIDAYS[d] && <span className="hol"> {KR_HOLIDAYS[d]}</span>} <span className="muted small">{list.length}건</span></h2>
            {evs.map((e) => <p key={e.id} className="small"><span className={`allday allday-${e.kind}`}>{e.staffId && <b>{staffById.get(e.staffId)?.name} </b>}{e.title}</span></p>)}
            {list.length === 0 ? <p className="muted small">일정 없음</p> : (
              <ul className="agenda-list">
                {list.map((s) => {
                  const st = staffById.get(s.therapistId);
                  return (
                    <li key={s.id}>
                      <button type="button" className={`agenda-item mg-${statusKey(s)}`} style={{ '--h': st?.hue ?? 220 } as CSSProperties} onClick={() => onOpen(s)}>
                        <span className="dot" aria-hidden="true" />
                        <span className="mono">{hm(s.startAt)}–{hm(s.endAt)}</span>
                        <strong>{clientName.get(s.clientId) ?? '이용자'}</strong>
                        <span>{s.program}</span>
                        <span className="muted small">{st?.name}{s.voucherProgram ? ` · ${s.voucherProgram}` : ' · 비바우처'}</span>
                        <span className={`chip chip-${s.status === 'COMPLETED' ? 'ok' : s.status === 'SCHEDULED' ? 'muted' : s.status === 'NO_SHOW' ? 'danger' : 'warn'}`}>{statusText(s)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** 휴대폰 일정 머리: 달 제목(눌러 날짜 고르기)·오늘·이전/다음·보기 알약·떠 있는 일정 추가 버튼. 넓은 화면에서는 CSS 가 숨긴다. */
function CalendarMobileBar({ title, date, view, onView, onDate, onToday, onMove, canAdd, ready, onAdd }: {
  title: string; date: string; view: View; onView: (v: View) => void; onDate: (d: string) => void; onToday: () => void; onMove: (dir: -1 | 1) => void;
  canAdd: boolean; ready: boolean; onAdd: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="calendar-mobile-bar">
      <div className="calendar-mobile-heading">
        <button type="button" className="calendar-month-button" aria-expanded={expanded} aria-controls="calendar-date-picker" onClick={() => setExpanded(!expanded)}>
          <span>{title}</span><span aria-hidden="true">{expanded ? '⌃' : '⌄'}</span>
        </button>
        <button type="button" className="btn calendar-today" onClick={onToday}>오늘</button>
      </div>
      {expanded && (
        <label id="calendar-date-picker" className="calendar-date-picker">
          <span>이동할 날짜</span>
          <input type="date" max="9999-12-31" value={date} onChange={(e) => { if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) { onDate(e.target.value); setExpanded(false); } }} />
        </label>
      )}
      <div className="calendar-mobile-controls">
        <button type="button" className="btn" aria-label="이전 기간" onClick={() => onMove(-1)}>‹</button>
        <div className="calendar-view-pills" role="group" aria-label="일정 보기">
          {([['list', '일정'], ['month', '월'], ['week', '주'], ['staff', '선생님']] as const).map(([v, label]) => (
            <button key={v} type="button" aria-pressed={view === v} onClick={() => onView(v)}>{label}</button>
          ))}
        </div>
        <button type="button" className="btn" aria-label="다음 기간" onClick={() => onMove(1)}>›</button>
      </div>
      {canAdd && (
        <button type="button" className="calendar-add-fab" disabled={!ready} onClick={onAdd} aria-label="선택한 날짜에 일정 추가">
          <span aria-hidden="true">+</span><span>일정 추가</span>
        </button>
      )}
    </div>
  );
}

/** 왼쪽 미니 달력: 날짜를 누르면 그 날짜로 이동, 일정 있는 날은 점 표시 */
function MiniMonth({ cursor, today, busyDates, onPick }: { cursor: string; today: string; busyDates: Set<string>; onPick: (d: string) => void }) {
  const [month, setMonth] = useState(`${monthOf(cursor)}-01`);
  useEffect(() => setMonth(`${monthOf(cursor)}-01`), [cursor]);
  const start = addDays(month, -((weekday(month) + 0) % 7)); // 일요일 시작
  const cells = Array.from({ length: 42 }, (_, i) => addDays(start, i));
  return (
    <section className="mini" aria-label="날짜 선택 달력">
      <div className="mini-head">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMonth(addMonths(month, -1))} aria-label="이전 달">‹</button>
        <strong>{month.slice(0, 4)}-{month.slice(5, 7)}</strong>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMonth(addMonths(month, 1))} aria-label="다음 달">›</button>
      </div>
      <div className="mini-grid">
        {WEEKDAY.map((w) => <span key={w} className={`mini-wd${w === '일' ? ' red' : ''}`}>{w}</span>)}
        {cells.map((d) => (
          <button key={d} type="button" onClick={() => onPick(d)} aria-label={dayLabel(d)} aria-current={d === cursor ? 'date' : undefined}
            className={`mini-day${monthOf(d) !== monthOf(month) ? ' out' : ''}${d === today ? ' today' : ''}${d === cursor ? ' sel' : ''}${KR_HOLIDAYS[d] || weekday(d) === 0 ? ' red' : ''}`}>
            {Number(d.slice(8))}{busyDates.has(d) && <span className="mini-dot" aria-hidden="true" />}
          </button>
        ))}
      </div>
    </section>
  );
}

/** 왼쪽 패널 하나(제목 줄 + 내용). 오른쪽 버튼은 그 패널의 초기화 동작. */
function Panel({ title, onReset, resetLabel, resetText, children }: { title: string; onReset?: () => void; resetLabel?: string; resetText?: boolean; children: ReactNode }) {
  return (
    <section className="schedule-panel cal-block" aria-label={title}>
      <div className="schedule-panel-heading">
        <span>{title}</span>
        {onReset && <button type="button" className={`link${resetText ? ' small' : ''}`} onClick={onReset} aria-label={resetLabel} title={resetLabel}>{resetText ? resetLabel : '↺'}</button>}
      </div>
      <div className="schedule-panel-content">{children}</div>
    </section>
  );
}
